// Media Registry - Central location for all media references (images and videos)
// This file should be the single source of truth for all media used in the application
// Keys are organized by WHERE the media is used, not WHAT the media contains
// To change any media used anywhere in the app, simply update the path in this registry
// Supports both images (.jpg, .png, .webp, etc.) and videos (.mp4, .webm, .ogg, .mov)

import { ReactComponent as LogoSVG } from '../assets/logos/the-sol-room-medium-logo.svg';
import heroImage from '../assets/heros/jia-sylvia-cadillac-large.jpg';
import jiaLiPhoto from '../assets/instructor-photos/jia-li.jpg';
import jiaLiFullPhoto from '../assets/instructor-photos/jia-li-full.jpg';
import studioCouchImage from '../assets/lifestyle/studio-couch.jpg';

// Movement images
import reformerImage1 from '../assets/movements/jia-reformer-1.jpg';
import reformerImage2 from '../assets/movements/jia-reformer-2.jpg';
import reformerImage3 from '../assets/movements/jia-reformer-3.jpg';
import reformerImage4 from '../assets/movements/jia-reformer-4.jpg';
import reformerImage5 from '../assets/movements/jia-reformer-5.jpg';
import reformerImage6 from '../assets/movements/jia-reformer-6.jpg';
import gyroImage1 from '../assets/movements/jia-gyro-1.jpg';
import gyroImage2 from '../assets/movements/jia-gyro-2.jpg';
import gyroImage3 from '../assets/movements/jia-gyro-3.jpg';
import gyroImage4 from '../assets/movements/jia-gyro-4.jpg';
import gyroImage5 from '../assets/movements/jia-gyro-5.jpg';
import gyroImage6 from '../assets/movements/jia-gyro-6.jpg';
import gyroImage7 from '../assets/movements/jia-gyro-7.jpg';
import cadillacImage1 from '../assets/movements/cadillac-1.jpg';
import cadillacImage2 from '../assets/movements/cadillac-2.jpg';
import cadillacImage3 from '../assets/movements/cadillac-3.jpg';
import cadillacImage4 from '../assets/movements/cadillac-4.jpg';
import cadillacImage5 from '../assets/movements/cadillac-5.jpg';
import matImage1 from '../assets/movements/mat-1.jpg';
import matImage2 from '../assets/movements/mat-2.jpg';
import matImage3 from '../assets/movements/mat-3.jpg';
import privateImage1 from '../assets/movements/private-1.jpg';
import privateImage2 from '../assets/movements/private-2.jpg';
import privateImage3 from '../assets/movements/private-3.jpg';

// Studio images
import studioImage1 from '../assets/studio/studio-1.jpg';
import studioImage2 from '../assets/studio/studio-2.jpg';
import studioImage3 from '../assets/studio/studio-3.jpg';
import studioImage4 from '../assets/studio/studio-4.jpg';
import studioImage5 from '../assets/studio/studio-5.jpg';

// Component exports (for SVGs that need to be used as React components)
export const components = {
  // Used in Navigation and Footer
  site_logo: LogoSVG,
};

// Image paths (organized by usage location, not image content)
export const images = {
  // HomePage - Hero Section
  home_page_hero_background: heroImage,
  
  // HomePage - "Join the Family" section
  home_page_join_the_family: studioCouchImage,
  
  // OurMovements - Reformer images
  movements_reformer_image_1: reformerImage1,
  movements_reformer_image_2: reformerImage2,
  movements_reformer_image_3: reformerImage3,
  movements_reformer_image_4: reformerImage4,
  movements_reformer_image_5: reformerImage5,
  movements_reformer_image_6: reformerImage6,
  
  // OurMovements - Gyrotonic images
  movements_gyrotonic_image_1: gyroImage1,
  movements_gyrotonic_image_2: gyroImage2,
  movements_gyrotonic_image_3: gyroImage3,
  movements_gyrotonic_image_4: gyroImage4,
  movements_gyrotonic_image_5: gyroImage5,
  movements_gyrotonic_image_6: gyroImage6,
  movements_gyrotonic_image_7: gyroImage7,
  
  // OurMovements - Cadillac images
  movements_cadillac_image_1: cadillacImage1,
  movements_cadillac_image_2: cadillacImage2,
  movements_cadillac_image_3: cadillacImage3,
  movements_cadillac_image_4: cadillacImage4,
  movements_cadillac_image_5: cadillacImage5,
  
  // OurMovements - Mat images
  movements_mat_image_1: matImage1,
  movements_mat_image_2: matImage2,
  movements_mat_image_3: matImage3,
  
  // OurMovements - Privates images
  movements_privates_image_1: privateImage1,
  movements_privates_image_2: privateImage2,
  movements_privates_image_3: privateImage3,
  
  // Instructor photos
  instructor_jia_li_gallery: jiaLiPhoto,
  instructor_jia_li_detail_page: jiaLiFullPhoto,
  
  // Purchase page - Store items
  purchase_page_grippy_socks: "https://images.unsplash.com/photo-1600185365484-3c5a4b2e0b1f?w=300&h=300&fit=crop",
};

// Dynamic studio gallery - add/remove images here to automatically update all galleries
export const studioGallery = [
  studioImage1,
  studioImage2,
  studioImage3,
  studioImage4,
  studioImage5,
];

// Dynamic movement galleries - add/remove images here to automatically update movement displays
// Used in: OurMovements component, MovementGalleryPage, movements config
export const reformerGallery = [
  reformerImage1,
  reformerImage2,
  reformerImage3,
  reformerImage4,
  reformerImage5,
  reformerImage6,
];

// Used in: OurMovements component, MovementGalleryPage, movements config
export const gyrotonicGallery = [
  gyroImage1,
  gyroImage2,
  gyroImage3,
  gyroImage4,
  gyroImage5,
  gyroImage6,
  gyroImage7,
];

// Used in: OurMovements component, MovementGalleryPage, movements config
export const cadillacGallery = [
  cadillacImage1,
  cadillacImage2,
  cadillacImage3,
  cadillacImage4,
  cadillacImage5,
];

// Used in: OurMovements component, MovementGalleryPage, movements config
export const matGallery = [
  matImage1,
  matImage2,
  matImage3,
];

// Used in: OurMovements component, MovementGalleryPage, movements config
export const privatesGallery = [
  privateImage1,
  privateImage2,
  privateImage3,
];

// Helper function to get image path
export const getImage = (key) => {
  return images[key];
};

// Helper function to get component
export const getComponent = (key) => {
  return components[key];
};

// Helper function to get studio gallery
export const getStudioGallery = () => {
  return studioGallery;
};

// Helper functions to get movement galleries
export const getReformerGallery = () => {
  return reformerGallery;
};

export const getGyrotonicGallery = () => {
  return gyrotonicGallery;
};

export const getCadillacGallery = () => {
  return cadillacGallery;
};

export const getMatGallery = () => {
  return matGallery;
};

export const getPrivatesGallery = () => {
  return privatesGallery;
};

// Helper function to get movement gallery by key
export const getMovementGallery = (movementKey) => {
  const galleries = {
    reformer: reformerGallery,
    gyrotonic: gyrotonicGallery,
    cadillac: cadillacGallery,
    mat: matGallery,
    privates: privatesGallery,
  };
  return galleries[movementKey] || [];
};

const imageRegistry = {
  images,
  components,
  getImage,
  getComponent,
};

export default imageRegistry;